import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

export interface Crumb {
  label: string;
  to?: string;
}

// item 456 — breadcrumbs moved into the STICKY header: pages don't render their
// trail inline anymore; they REGISTER it here (keyed by their route's pathname) and
// the shell header displays it. Path-keying matters for item 455's layer system:
// a canvas page and a layer-2 panel page can be mounted at the same time, each
// registering its own trail — the header shows the canvas path's trail, the panel
// shows its own.
interface BreadcrumbsContextValue {
  trails: Record<string, Crumb[]>;
  register: (path: string, crumbs: Crumb[]) => void;
  unregister: (path: string) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue>({
  trails: {},
  register: () => {},
  unregister: () => {},
});

export const BreadcrumbsProvider: React.FC = ({ children }) => {
  const [trails, setTrails] = useState<Record<string, Crumb[]>>({});
  const register = useCallback((path: string, crumbs: Crumb[]) => {
    setTrails((t) => ({ ...t, [path]: crumbs }));
  }, []);
  const unregister = useCallback((path: string) => {
    setTrails((t) => {
      const { [path]: gone, ...rest } = t;
      return rest;
    });
  }, []);
  const value = useMemo(() => ({ trails, register, unregister }), [trails, register, unregister]);
  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
};

export const useBreadcrumbs = () => useContext(BreadcrumbsContext);
